import { lazy, Suspense, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { moduleClients } from '@/features/clients';
import { moduleGdpr } from '@/features/gdpr';
import { modulePayments } from '@/features/payments';
import { modulePlatform } from '@/features/platform';
import { moduleSales } from '@/features/sales';
import { moduleWorkflows } from '@/features/workflows';
import { GardeAnonyme, GardeAuthentifie, GardeRole } from '@/shared/auth/GardeRoute';
import { useSession } from '@/shared/auth/sessionStore';
import { EtatVide } from '@/shared/components/Etats';
import { Bouton } from '@/shared/ui/Bouton';
import { useModulesActives } from '@/shared/tenant/useEntreprise';
import { Squelette } from '@/shared/ui/Surface';

import {
  cheminParDefaut,
  resoudreModules,
  routesActives,
  type DeclarationModule,
} from './moduleRegistry';

/**
 * La page de connexion est importee STATIQUEMENT, a rebours du reste.
 *
 * C'est la premiere page que voit un visiteur non authentifie. La charger
 * paresseusement ajoutait un aller-retour reseau sur le chemin critique : le
 * navigateur devait recuperer l'entree, la lire, puis seulement demander la page.
 * Sur mobile bride, ce saut coutait a lui seul plusieurs centaines de
 * millisecondes de LCP. Elle est legere, elle voyage donc avec l'entree.
 *
 * L'inscription, elle, reste paresseuse : on n'y arrive que par un clic.
 */
import ConnexionPage from '@/features/auth/ConnexionPage';

const ReinitialisationPage = lazy(() => import('@/features/auth/ReinitialisationPage'));
const MotDePasseOubliePage = lazy(() => import('@/features/auth/MotDePasseOubliePage'));
const SouscriptionMerciPage = lazy(() => import('@/features/payments/pages/SouscriptionMerciPage'));

const InscriptionPage = lazy(async () => ({
  default: (await import('@/features/auth/ConnexionPage')).InscriptionPage,
}));

/**
 * La coquille est chargee PARESSEUSEMENT, et ce n'est pas un detail.
 *
 * Elle entraine avec elle la bibliotheque d'animation (~133 ko) et toute la
 * navigation. Un visiteur non connecte n'en a aucun usage : l'importer
 * statiquement mettait ce poids sur le chemin critique de la page de connexion et
 * faisait tomber le score mobile a 87, sous le seuil de 95 exige au §7.
 */
const Coquille = lazy(async () => ({ default: (await import('./Coquille')).Coquille }));

/**
 * TOUS les modules connus du front.
 *
 * Ajouter un module = ajouter une ligne ici. Le reste — entree de menu, routes,
 * chargement paresseux — decoule de sa declaration (§3).
 */
const MODULES: readonly DeclarationModule[] = [
  moduleClients,
  moduleSales,
  modulePayments,
  moduleWorkflows,
  moduleGdpr,
  modulePlatform,
];

/**
 * Page publique de suivi partage.
 *
 * Hors du registre et hors de la coquille : elle n'a ni session, ni entreprise
 * resolue, ni activation de module a consulter. C'est le back qui decide, a
 * partir du seul jeton, si le suivi existe encore (§6).
 */
const SuiviPublicPage = lazy(() => import('@/features/workflows/pages/SuiviPublicPage'));

function PageIntrouvable() {
  return (
    <EtatVide
      titre="Page introuvable"
      description="Cette page n'existe pas, ou le module correspondant n'est pas actif pour cette entreprise."
      action={
        <Bouton asChild variante="secondaire" taille="sm">
          <a href="/">Retour a l'accueil</a>
        </Bouton>
      }
    />
  );
}

/**
 * Routes de l'application authentifiee.
 *
 * Le routeur n'enregistre QUE les routes des modules actifs. Une route d'un module
 * desactive n'existe donc pas : la saisir a la main tombe sur « page introuvable »,
 * et non sur une page servie derriere un menu masque (§3).
 */
function RoutesApplication() {
  /*
   * `isLoading` ET NON `isPending`, et la nuance a coute un ecran blanc.
   *
   * `useModulesActives` est desactivee tant qu'aucune entreprise n'est active — il n'y
   * a rien a demander. Or une requete desactivee reste eternellement `isPending` dans
   * TanStack Query : bloquer dessus laissait un compte neuf, ou tout compte n'ayant pas
   * encore choisi son entreprise, devant un squelette qui ne partait jamais. Le module
   * `platform` est `toujoursActif` precisement pour offrir la sortie de ce cul-de-sac,
   * et cette garde l'annulait.
   *
   * `isLoading` vaut `isPending && isFetching` : faux pour une requete desactivee, vrai
   * pendant un vrai chargement. C'est exactement la question posee ici — « attend-on
   * quelque chose ? » — et non « a-t-on deja une reponse ? ».
   *
   * Trouve par le parcours Playwright, pas par la lecture du code.
   */
  const { data: activations, isLoading } = useModulesActives();
  const role = useSession((etat) => etat.role);

  const modules = useMemo(
    () => resoudreModules(MODULES, activations, role),
    [activations, role],
  );

  const routes = useMemo(() => routesActives(modules), [modules]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-fond">
        <Squelette className="h-32 w-64" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Coquille modules={modules} />}>
        <Route index element={<Navigate to={cheminParDefaut(modules)} replace />} />

        {routes.map((route) => {
          const Page = route.composant;

          const element = route.roleRequis ? (
            <GardeRole requis={route.roleRequis}>
              <Page />
            </GardeRole>
          ) : (
            <Page />
          );

          return <Route key={route.chemin} path={route.chemin} element={element} />;
        })}

        <Route path="*" element={<PageIntrouvable />} />
      </Route>
    </Routes>
  );
}

/** Attente pendant le chargement paresseux d'une page publique. */
function Attente() {
  return <div className="min-h-dvh bg-fond" aria-busy />;
}

export function Routeur() {
  const emplacement = useLocation();

  // Les pages publiques sont montees HORS de la coquille authentifiee : la page de
  // suivi partage ne doit rien charger de l'application, ni la barre laterale, ni
  // le contexte d'entreprise (§6).
  const estPublique =
    emplacement.pathname.startsWith('/connexion') ||
    emplacement.pathname.startsWith('/inscription') ||
    emplacement.pathname.startsWith('/mot-de-passe-oublie') ||
    emplacement.pathname.startsWith('/souscription/') ||
    emplacement.pathname.startsWith('/reinitialisation') ||
    emplacement.pathname.startsWith('/suivi/');

  if (estPublique) {
    return (
      <Suspense fallback={<Attente />}>
        <Routes>
          <Route
            path="/connexion"
            element={
              <GardeAnonyme>
                <ConnexionPage />
              </GardeAnonyme>
            }
          />

          <Route
            path="/inscription"
            element={
              <GardeAnonyme>
                <InscriptionPage />
              </GardeAnonyme>
            }
          />

          {/*
            Pas de garde : celui qui a oublie son mot de passe ne peut pas
            s'authentifier pour le changer. C'est le jeton de l'URL qui fait
            autorite, et l'API seule le verifie.
          */}
          {/* Meme raison : sans mot de passe, on ne peut pas s'authentifier pour en demander un. */}
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOubliePage />} />

          <Route path="/reinitialisation" element={<ReinitialisationPage />} />

          {/*
            Retour du CLIENT apres son paiement. Il n'a pas de compte Astrolabe et
            n'a pas a en avoir : la page est publique, et parle au nom de son
            prestataire.
          */}
          <Route path="/souscription/merci" element={<SouscriptionMerciPage />} />

          {/* Pas de garde : accessible connecte comme deconnecte, c'est le but. */}
          <Route path="/suivi/:jeton" element={<SuiviPublicPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <GardeAuthentifie>
      <RoutesApplication />
    </GardeAuthentifie>
  );
}

export { MODULES };
